import { Drawer, Form, Input, Button, DatePicker, Select, Space, message, Upload, Image as AntImage } from 'antd';
import { useState, useEffect } from 'react';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from '../config/axios';
import { handleAPIError } from '../utils/helpers';
import dayjs from '../config/dayjs';
import { User } from '../types/user.types';
import type { RcFile, UploadProps } from 'antd/es/upload/interface';

interface EditUserProps {
	open: boolean;
	onClose: () => void;
	userId: number;
	onSuccess: () => void;
	userData?: User;
}

interface UserFormValues {
	first_name: string;
	last_name: string;
	phone_number: string;
	email?: string;
	district: string;
	town: string;
	date_of_birth?: dayjs.Dayjs;
	nin?: string;
	sex: string;
	next_of_kin?: string;
	dp?: string;
	nid_front?: string;
	nid_back?: string;
}

// Type for the custom request function
type CustomRequest = UploadProps['customRequest'];

export const EditUser = ({ open, onClose, userId, onSuccess, userData }: EditUserProps) => {
	const [form] = Form.useForm<UserFormValues>();
	const [loading, setLoading] = useState(false);
	const [dpUploading, setDpUploading] = useState(false);
	const [nidFrontUploading, setNidFrontUploading] = useState(false);
	const [nidBackUploading, setNidBackUploading] = useState(false);
	const [dpUrl, setDpUrl] = useState<string | undefined>(undefined);
	const [nidFrontUrl, setNidFrontUrl] = useState<string | undefined>(undefined);
	const [nidBackUrl, setNidBackUrl] = useState<string | undefined>(undefined);

	// Initialize form with user data when available
	useEffect(() => {
		if (userData && open) {
			// Extract only the fields needed for the form
			const formValues: UserFormValues = {
				first_name: userData.first_name,
				last_name: userData.last_name,
				phone_number: userData.phone_number,
				email: userData.email,
				district: userData.district,
				town: userData.town,
				date_of_birth: userData.date_of_birth ? dayjs(userData.date_of_birth) : undefined,
				nin: userData.nin,
				sex: userData.sex,
				next_of_kin: userData.next_of_kin,
				dp: userData.dp || undefined,
				nid_front: userData.nid_front || undefined,
				nid_back: userData.nid_back || undefined,
			};

			form.setFieldsValue(formValues);

			// Initialize image URLs if available
			setDpUrl(userData.dp || undefined);
			setNidFrontUrl(userData.nid_front || undefined);
			setNidBackUrl(userData.nid_back || undefined);
		}
	}, [userData, open, form]);

	const handleSubmit = async (values: UserFormValues) => {
		setLoading(true);
		try {
			// Format date to ISO string if it exists
			const formattedValues = {
				...values,
				date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : undefined,
				// Add URLs from state
				dp: dpUrl || values.dp,
				nid_front: nidFrontUrl || values.nid_front,
				nid_back: nidBackUrl || values.nid_back,
			};

			await axios.patch(`/admin/users/${userId}`, formattedValues, {
				withCredentials: true,
			});

			message.success('User updated successfully');
			onSuccess();
			onClose();
		} catch (err) {
			handleAPIError(err as Error);
		} finally {
			setLoading(false);
		}
	};

	const uploadImage = async (file: RcFile, type: 'dp' | 'nid_front' | 'nid_back') => {
		const formData = new FormData();
		formData.append('file', file);

		// Set the corresponding loading state
		switch (type) {
			case 'dp':
				setDpUploading(true);
				break;
			case 'nid_front':
				setNidFrontUploading(true);
				break;
			case 'nid_back':
				setNidBackUploading(true);
				break;
		}

		try {
			const response = await axios.post('/fundamentals/file-upload', formData, { withCredentials: true });

			// Update the corresponding URL state with secure_url
			switch (type) {
				case 'dp':
					setDpUrl(response.data.results.secure_url);
					form.setFieldValue('dp', response.data.results.secure_url);
					break;
				case 'nid_front':
					setNidFrontUrl(response.data.results.secure_url);
					form.setFieldValue('nid_front', response.data.results.secure_url);
					break;
				case 'nid_back':
					setNidBackUrl(response.data.results.secure_url);
					form.setFieldValue('nid_back', response.data.results.secure_url);
					break;
			}

			message.success(`${file.name} uploaded successfully`);
			return response.data.results.secure_url;
		} catch (err) {
			message.error(`${file.name} upload failed`);
			handleAPIError(err as Error);
			return '';
		} finally {
			// Reset the corresponding loading state
			switch (type) {
				case 'dp':
					setDpUploading(false);
					break;
				case 'nid_front':
					setNidFrontUploading(false);
					break;
				case 'nid_back':
					setNidBackUploading(false);
					break;
			}
		}
	};

	const beforeUpload = (file: RcFile) => {
		const isImage = file.type.startsWith('image/');
		if (!isImage) {
			message.error('You can only upload image files!');
		}

		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error('Image must be smaller than 2MB!');
		}

		return isImage && isLt2M;
	};

	// Handle upload for different image types
	const handleDpUpload: CustomRequest = async (options) => {
		const { file, onSuccess, onError } = options;
		try {
			await uploadImage(file as RcFile, 'dp');
			onSuccess?.(null);
		} catch {
			onError?.(new Error('Upload failed'));
		}
	};

	const handleNidFrontUpload: CustomRequest = async (options) => {
		const { file, onSuccess, onError } = options;
		try {
			await uploadImage(file as RcFile, 'nid_front');
			onSuccess?.(null);
		} catch {
			onError?.(new Error('Upload failed'));
		}
	};

	const handleNidBackUpload: CustomRequest = async (options) => {
		const { file, onSuccess, onError } = options;
		try {
			await uploadImage(file as RcFile, 'nid_back');
			onSuccess?.(null);
		} catch {
			onError?.(new Error('Upload failed'));
		}
	};

	return (
		<Drawer
			title="Edit User"
			placement="right"
			onClose={onClose}
			open={open}
			width={500}
			footer={
				<Space>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						type="primary"
						loading={loading}
						onClick={() => form.submit()}
					>
						Update
					</Button>
				</Space>
			}
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				className="mt-4"
			>
				<div className="grid grid-cols-2 gap-4">
					<Form.Item
						name="first_name"
						label="First Name"
						rules={[{ required: true, message: 'First name is required' }]}
					>
						<Input placeholder="First Name" />
					</Form.Item>

					<Form.Item
						name="last_name"
						label="Last Name"
						rules={[{ required: true, message: 'Last name is required' }]}
					>
						<Input placeholder="Last Name" />
					</Form.Item>
				</div>

				<Form.Item
					name="phone_number"
					label="Phone Number"
					rules={[{ required: true, message: 'Phone number is required' }]}
				>
					<Input placeholder="Phone Number" />
				</Form.Item>

				<Form.Item
					name="email"
					label="Email"
					rules={[{ type: 'email', message: 'Not a valid email' }]}
				>
					<Input placeholder="Email" />
				</Form.Item>

				<div className="grid grid-cols-2 gap-4">
					<Form.Item
						name="district"
						label="District"
						rules={[{ required: true, message: 'District is required' }]}
					>
						<Input placeholder="District" />
					</Form.Item>

					<Form.Item
						name="town"
						label="Town"
						rules={[{ required: true, message: 'Town is required' }]}
					>
						<Input placeholder="Town" />
					</Form.Item>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<Form.Item
						name="date_of_birth"
						label="Date of Birth"
					>
						<DatePicker
							className="w-full"
							format="YYYY-MM-DD"
						/>
					</Form.Item>

					<Form.Item
						name="sex"
						label="Sex"
						rules={[{ required: true, message: 'Sex is required' }]}
					>
						<Select placeholder="Select sex">
							<Select.Option value="Male">Male</Select.Option>
							<Select.Option value="Female">Female</Select.Option>
							<Select.Option value="Other">Other</Select.Option>
						</Select>
					</Form.Item>
				</div>

				<Form.Item
					name="nin"
					label="National ID Number"
				>
					<Input placeholder="National ID Number" />
				</Form.Item>

				<Form.Item
					name="next_of_kin"
					label="Next of Kin"
				>
					<Input placeholder="Next of Kin" />
				</Form.Item>

				{/* Image Upload Fields */}
				<Form.Item
					name="dp"
					label="Profile Picture"
					extra={
						dpUrl ? (
							<div className="mt-4">
								<AntImage
									src={dpUrl}
									alt="Profile Picture"
									width={150}
									height="auto"
									className="rounded-lg"
								/>
							</div>
						) : null
					}
				>
					<Upload
						name="dp"
						listType="picture"
						maxCount={1}
						beforeUpload={beforeUpload}
						customRequest={handleDpUpload}
						showUploadList={false}
					>
						<Button
							icon={dpUploading ? <LoadingOutlined /> : <UploadOutlined />}
							disabled={dpUploading}
						>
							{dpUploading ? 'Uploading' : 'Upload Profile Picture'}
						</Button>
					</Upload>
					{dpUrl && <div className="mt-2 text-sm text-green-600">Image uploaded successfully</div>}
				</Form.Item>

				<Form.Item
					name="nid_front"
					label="National ID (Front)"
					extra={
						nidFrontUrl ? (
							<div className="mt-4">
								<AntImage
									src={nidFrontUrl}
									alt="ID Front"
									width={200}
									height="auto"
									className="rounded-lg"
								/>
							</div>
						) : null
					}
				>
					<Upload
						name="nid_front"
						listType="picture"
						maxCount={1}
						beforeUpload={beforeUpload}
						customRequest={handleNidFrontUpload}
						showUploadList={false}
					>
						<Button
							icon={nidFrontUploading ? <LoadingOutlined /> : <UploadOutlined />}
							disabled={nidFrontUploading}
						>
							{nidFrontUploading ? 'Uploading' : 'Upload ID Front'}
						</Button>
					</Upload>
					{nidFrontUrl && <div className="mt-2 text-sm text-green-600">Image uploaded successfully</div>}
				</Form.Item>

				<Form.Item
					name="nid_back"
					label="National ID (Back)"
					extra={
						nidBackUrl ? (
							<div className="mt-4">
								<AntImage
									src={nidBackUrl}
									alt="ID Back"
									width={200}
									height="auto"
									className="rounded-lg"
								/>
							</div>
						) : null
					}
				>
					<Upload
						name="nid_back"
						listType="picture"
						maxCount={1}
						beforeUpload={beforeUpload}
						customRequest={handleNidBackUpload}
						showUploadList={false}
					>
						<Button
							icon={nidBackUploading ? <LoadingOutlined /> : <UploadOutlined />}
							disabled={nidBackUploading}
						>
							{nidBackUploading ? 'Uploading' : 'Upload ID Back'}
						</Button>
					</Upload>
					{nidBackUrl && <div className="mt-2 text-sm text-green-600">Image uploaded successfully</div>}
				</Form.Item>
			</Form>
		</Drawer>
	);
};

export default EditUser;
